import React from 'react';
import PropTypes from 'prop-types';

import Modal from 'common/components/Modal';
import Icon from 'common/components/Icon';
import Typography from 'common/components/Typography';
import LoadingPage from 'common/components/LoadingPage';
import colors from 'common/styles/colors';

import styles from './index.module.css';

const RewardDetailsModal = ({
  isOpen,
  onClose,
  rewardItem,
  settlementData = null,
  isLoading = false,
}) => {
  const data =
    settlementData && settlementData.length > 0 ? settlementData : [];

  if (!rewardItem) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnClickOutside
      closeOnEscape
      modalClassName={styles.modal}
    >
      <div className={styles.content}>
        {isLoading ? (
          <LoadingPage />
        ) : (
          <>
            <div className={styles.header}>
              <div className={styles.headerTop}>
                <Typography
                  tag="h2"
                  size="lg"
                  weight="semiBold"
                  color="color-neutral-900"
                >
                  {rewardItem.period}
                </Typography>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  <Icon.CrossV2
                    size="medium"
                    fill={colors['rebranding-md-color-grey-50']}
                    description="Close"
                  />
                </button>
              </div>
              <div className={styles.headerBottom}>
                <div className={styles.infoBoxes}>
                  <div className={styles.infoBox}>
                    <div className={styles.infoBoxHeader}>
                      <Icon.Lock2
                        className={styles.lockIcon}
                        size="xSmall"
                        fill={colors['color-neutral-900']}
                        description="Lockup start"
                      />
                      <Typography
                        size="xs"
                        weight="semiBold"
                        color="color-neutral-900"
                      >
                        Lockup start
                      </Typography>
                    </div>
                    <Typography size="sm" color="color-neutral-900">
                      {rewardItem.startDate}
                    </Typography>
                  </div>
                  <div className={styles.infoBox}>
                    <div className={styles.infoBoxHeader}>
                      <Icon.Lock2OpenedDuotone
                        className={styles.unlockIcon}
                        size="xSmall"
                        fill={colors['color-green-500']}
                        description="Unlock date"
                      />
                      <Typography
                        size="xs"
                        weight="semiBold"
                        color="color-neutral-900"
                      >
                        Unlock date
                      </Typography>
                    </div>
                    <Typography size="sm" color="color-neutral-900">
                      {rewardItem.unlockDate}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.tableSection}>
              <div className={styles.tableWrapper}>
                <div className={`${styles.table} ${styles.tableDesktop}`}>
                  <div className={styles.tableColumn}>
                    <div className={styles.tableHeader}>
                      <Typography
                        size="sm"
                        weight="semiBold"
                        color="color-neutral-900"
                      >
                        Settlement date
                      </Typography>
                    </div>
                    {data.map((item) => (
                      <div
                        key={`settlement-date-${item.id}`}
                        className={styles.tableCell}
                      >
                        <Typography size="sm" color="color-neutral-900">
                          {item.settlementDate}
                        </Typography>
                      </div>
                    ))}
                  </div>
                  <div className={styles.tableColumn}>
                    <div className={styles.tableHeader}>
                      <Typography
                        size="sm"
                        weight="semiBold"
                        color="color-neutral-900"
                        className={styles.textRight}
                      >
                        Settlement fee
                      </Typography>
                    </div>
                    {data.map((item) => (
                      <div
                        key={`settlement-fee-${item.id}`}
                        className={styles.tableCell}
                      >
                        <Typography
                          size="sm"
                          color="color-neutral-900"
                          className={styles.textRight}
                        >
                          {item.settlementFee}
                        </Typography>
                      </div>
                    ))}
                  </div>
                  <div className={styles.tableColumn}>
                    <div className={styles.tableHeader}>
                      <Typography
                        size="sm"
                        weight="semiBold"
                        color="color-neutral-900"
                        className={styles.textRight}
                      >
                        Rate
                      </Typography>
                    </div>
                    {data.map((item) => (
                      <div
                        key={`rate-${item.id}`}
                        className={styles.tableCell}
                      >
                        <Typography
                          size="sm"
                          color="color-neutral-900"
                          className={styles.textRight}
                        >
                          {item.rate}
                        </Typography>
                      </div>
                    ))}
                  </div>
                  <div className={styles.tableColumn}>
                    <div className={styles.tableHeader}>
                      <Typography
                        size="sm"
                        weight="semiBold"
                        color="color-neutral-900"
                        className={styles.textRight}
                      >
                        Earned
                      </Typography>
                    </div>
                    {data.map((item) => (
                      <div
                        key={`earned-${item.id}`}
                        className={styles.tableCell}
                      >
                        <Typography
                          size="sm"
                          color="color-neutral-900"
                          className={styles.textRight}
                        >
                          {item.earnedXmn}
                        </Typography>
                        <Typography
                          size="xs"
                          color="color-neutral-500"
                          className={styles.textRight}
                        >
                          {item.earnedSettlement}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.tableMobile}>
                  {data.map((item) => (
                    <div
                      key={`mobile-row-${item.id}`}
                      className={styles.mobileRow}
                    >
                      <div className={styles.mobileColumn}>
                        <Typography size="sm" color="color-neutral-900">
                          {item.earnedXmn}
                        </Typography>
                        <Typography size="xs" color="color-neutral-500">
                          {item.earnedSettlement}
                        </Typography>
                      </div>
                      <div
                        className={`${styles.mobileColumn} ${styles.mobileColumnRight}`}
                      >
                        <Typography size="sm" color="color-neutral-900">
                          {item.settlementDate}
                        </Typography>
                        <Typography size="xs" color="color-neutral-500">
                          {item.settlementFee} fee · {item.rate} rate
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

RewardDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rewardItem: PropTypes.shape({
    period: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    unlockDate: PropTypes.string.isRequired,
  }),
  settlementData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      settlementDate: PropTypes.string.isRequired,
      settlementFee: PropTypes.string.isRequired,
      rate: PropTypes.string.isRequired,
      earnedSettlement: PropTypes.string.isRequired,
      earnedXmn: PropTypes.string.isRequired,
    })
  ),
  isLoading: PropTypes.bool,
};

RewardDetailsModal.defaultProps = {
  rewardItem: null,
  settlementData: null,
  isLoading: false,
};

export default RewardDetailsModal;
